import React, { useLayoutEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { Button } from 'reactstrap';
import { connect } from 'react-redux';
import { clearManualVouchers, getManualVouchers } from '../../../../actions';
import {
  MANUAL_VOUCHER_PATH,
  NEW_MANUAL_VOUCHER_CREDIT_NOTE_PATH,
  NEW_MANUAL_VOUCHER_DEBIT_NOTE_PATH,
  NEW_MANUAL_VOUCHER_PATH,
} from '../../../../config/paths';
import { DEFAULT_PAGE_SIZE } from '../../../../config/constants';
import ModuleList from '../../../layout/ModuleList';
import {
  generateCreditNoteManualVoucherColumns,
  generateManualVoucherColumns,
} from '../../../../config/columns';
import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';
import Table from '../../../common/Table';

const VOUCHER_VIEWS = {
  REGULAR: 'regular',
  CREDIT: 'credit',
  DEBIT: 'debit',
};

const VIEW_CONFIG = {
  [VOUCHER_VIEWS.REGULAR]: {
    title: 'Comprobantes Manuales',
    buttonPath: NEW_MANUAL_VOUCHER_PATH,
    buttonText: 'Crear comprobante manual',
    query: 'voucherTypeId!:83,voucherTypeId!:248',
    getColumns: () => generateManualVoucherColumns(true),
  },
  [VOUCHER_VIEWS.CREDIT]: {
    title: 'Notas de Crédito',
    buttonPath: NEW_MANUAL_VOUCHER_CREDIT_NOTE_PATH,
    buttonText: 'Crear nota de crédito',
    query: 'voucherTypeId:83',
    getColumns: () => generateCreditNoteManualVoucherColumns(true),
  },
  [VOUCHER_VIEWS.DEBIT]: {
    title: 'Notas de Débito',
    buttonPath: NEW_MANUAL_VOUCHER_DEBIT_NOTE_PATH,
    buttonText: 'Crear nota de débito',
    query: 'voucherTypeId:248',
    getColumns: () => generateCreditNoteManualVoucherColumns(false),
  },
};

export const ManualVouchers = ({
  breadcrumbs,
  vouchers,
  loading,
  dispatchGetManualVouchers,
  dispatchClearManualVouchers,
  showOnModal,
  getRowData,
  byBusiness,
}) => {
  const [voucherView, setVoucherView] = useState(VOUCHER_VIEWS.REGULAR);

  useLayoutEffect(
    () => () => {
      dispatchClearManualVouchers();
    },
    [],
  );

  const currentConfig = useMemo(() => VIEW_CONFIG[voucherView], [voucherView]);

  const changeVoucherView = (nextView) => {
    const resolvedView =
      voucherView === nextView ? VOUCHER_VIEWS.REGULAR : nextView;
    const nextQuery = VIEW_CONFIG[resolvedView].query;

    setVoucherView(resolvedView);
    dispatchGetManualVouchers({ query: nextQuery, sort: 'id,desc' });
  };

  const onFetchData = (data) => {
    const newData = { ...data };

    // TODO remove this hack when ModuleList implements multiple filter on a same column
    const businessTaxIdFound =
      newData.query &&
      newData.query.length &&
      newData.query.filter(
        (column) =>
          column.substring(0, column.indexOf(':')) === 'business.businessTaxId',
      );

    if (businessTaxIdFound && businessTaxIdFound[0]) {
      const businessTaxIdValue = businessTaxIdFound[0].substring(
        businessTaxIdFound[0].indexOf(':') + 1,
      );
      const businessTaxIdIndex = newData.query.indexOf(businessTaxIdFound[0]);
      const column =
        businessTaxIdValue.length === 8
          ? 'customer.idDocumentNumber'
          : 'business.businessTaxId';
      newData.query[businessTaxIdIndex] = `${column}:${businessTaxIdValue}`;
    }

    if (byBusiness)
      newData.query = [
        ...newData.query,
        `business.businessTaxId:${byBusiness}`,
        'paid:false',
      ];

    dispatchGetManualVouchers(newData);
  };

  const data = vouchers.get('content') || [];
  const pages = vouchers.get('totalPages') || null;
  const defaultPageSize = vouchers.get('size') || DEFAULT_PAGE_SIZE;

  const tableProps = {
    data,
    pages,
    defaultPageSize,
    filterable: true,
    fetchData: onFetchData,
    loading,
    navigateToModelOnRowClick: true,
  };

  if (showOnModal)
    return (
      <Table
        {...tableProps}
        columns={generateManualVoucherColumns(false)}
        getRowData={getRowData}
        highlightSelectedRow
      />
    );

  return (
    <ModuleList
      title={currentConfig.title}
      breadcrumbs={breadcrumbs}
      buttonPath={currentConfig.buttonPath}
      buttonText={currentConfig.buttonText}
      extraButtons={
        <>
          <Button
            type="button"
            color={
              voucherView === VOUCHER_VIEWS.CREDIT ? 'secondary' : 'success'
            }
            className="mr-2"
            onClick={() => changeVoucherView(VOUCHER_VIEWS.CREDIT)}
          >
            {voucherView === VOUCHER_VIEWS.CREDIT
              ? 'Mostrar boletas/facturas'
              : 'Mostrar notas de crédito'}
          </Button>
          <Button
            type="button"
            color={
              voucherView === VOUCHER_VIEWS.DEBIT ? 'secondary' : 'warning'
            }
            className="mr-2"
            onClick={() => changeVoucherView(VOUCHER_VIEWS.DEBIT)}
          >
            {voucherView === VOUCHER_VIEWS.DEBIT
              ? 'Mostrar boletas/facturas'
              : 'Mostrar notas de débito'}
          </Button>
        </>
      }
      tableStructure={{
        ...tableProps,
        modelPath: MANUAL_VOUCHER_PATH,
        columns: currentConfig.getColumns(),
        queryProps: currentConfig.query,
      }}
    />
  );
};

ManualVouchers.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  vouchers: PropTypes.instanceOf(Immutable.Map),
  loading: PropTypes.bool,
  dispatchGetManualVouchers: PropTypes.func.isRequired,
  dispatchClearManualVouchers: PropTypes.func.isRequired,
  showOnModal: PropTypes.bool,
  getRowData: PropTypes.func,
  byBusiness: PropTypes.string,
};

ManualVouchers.defaultProps = {
  vouchers: null,
  loading: false,
  showOnModal: false,
  getRowData: null,
  byBusiness: null,
};

const mapStateToProps = ({ AccountingUnit }) => ({
  breadcrumbs: [
    ...AccountingUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Comprobantes Manuales',
      href: MANUAL_VOUCHER_PATH,
    },
  ],
  vouchers: AccountingUnit.ManualVoucher.getIn(['all', 'content']),
  loading: AccountingUnit.ManualVoucher.getIn(['all', 'loading']),
});

const mapDispatchToProps = {
  dispatchGetManualVouchers: getManualVouchers,
  dispatchClearManualVouchers: clearManualVouchers,
};

export default connect(mapStateToProps, mapDispatchToProps)(ManualVouchers);
